import { useState, useEffect, useRef, useCallback } from 'react';

const API_URL = 'https://api.frankfurter.dev/v1/latest?from=USD';
const POLL_INTERVAL = 300000; // 5 min — rates don't change fast

export const CURRENCIES = [
  { code: 'USD', symbol: '$' },
  { code: 'EUR', symbol: '\u20AC' },
  { code: 'GBP', symbol: '\u00A3' },
  { code: 'JPY', symbol: '\u00A5' },
  { code: 'CAD', symbol: 'C$' },
  { code: 'AUD', symbol: 'A$' },
  { code: 'CHF', symbol: 'Fr' },
  { code: 'CNY', symbol: '\u00A5' },
  { code: 'BRL', symbol: 'R$' },
  { code: 'MXN', symbol: 'Mex$' },
  { code: 'KRW', symbol: '\u20A9' },
  { code: 'INR', symbol: '\u20B9' },
  { code: 'SEK', symbol: 'kr' },
];

export const useFxRates = () => {
  const [rates, setRates] = useState({ USD: 1 });
  const abortRef = useRef(null);
  const mountedRef = useRef(true);

  const fetchRates = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch(API_URL, { signal: controller.signal });
      if (!res.ok || !mountedRef.current) return;
      const json = await res.json();
      setRates({ USD: 1, ...json.rates });
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.warn('FX rates fetch failed:', err.message);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchRates();
    const timer = setInterval(fetchRates, POLL_INTERVAL);

    const handleOnline = () => fetchRates();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') fetchRates();
    };

    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      mountedRef.current = false;
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(timer);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchRates]);

  return rates;
};
